import React from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { QuestionResponse, TypedQuestion } from 'types/question-response.types';

import {
  useQuestionnaireSchema,
  QuestionnaireFormValues,
  initializeQuestionnaire
} from './useQuestionnaireSchema';

type UseQuestionResponseFormParams<K extends string> = {
  fieldName: K;
  questions: TypedQuestion[];
  responses: QuestionResponse[];
};

type QuestionResponseFormFieldValues<K extends string> = FieldValues & {
  [P in K]: QuestionnaireFormValues;
};

type UseQuestionResponseFormReturn<K extends string> = {
  form: UseFormReturn<QuestionResponseFormFieldValues<K>>;
  parseResponses: (formValues: QuestionResponseFormFieldValues<K>) => QuestionResponse[];
};

/**
 * Creates a react-hook-form instance for question responses under a dynamic field name.
 *
 * @param fieldName - The key under where questionnaire form values are stored
 * @param questions - The list of questions to build the form schema from
 * @param responses - Existing responses to initialize the form with
 *
 * Note: the response values will update if the `responses` param changes.
 */
export const useQuestionResponseForm = <K extends string>({
  fieldName,
  questions,
  responses
}: UseQuestionResponseFormParams<K>): UseQuestionResponseFormReturn<K> => {
  const { questionnaireSchema, parseQuestionnaireFormValues } = useQuestionnaireSchema({
    hasQuestionnaire: true,
    questions
  });
  const formSchema = z.object({
    [fieldName]: questionnaireSchema
  });
  const initialResponses: QuestionResponseFormFieldValues<K> = React.useMemo(
    () =>
      ({
        [fieldName]: initializeQuestionnaire(questions, responses)
      } as QuestionResponseFormFieldValues<K>),
    [questions, responses]
  );
  const form = useForm<QuestionResponseFormFieldValues<K>>({
    resolver: zodResolver(formSchema),
    values: initialResponses
  });

  const parseResponses = React.useCallback(
    (formValues: QuestionResponseFormFieldValues<K>) => {
      return parseQuestionnaireFormValues(formValues[fieldName]);
    },
    [parseQuestionnaireFormValues]
  );

  return {
    form,
    parseResponses
  };
};
